import invariant from "tiny-invariant";
import { ReadonlyDeep } from "type-fest";
import { trackEvent } from "../api/tracker";
import { getStoredExperiments } from "../experiment";
import { getMeristemState, Targeting } from "../meristemContext";
import { mapEventName } from "./event-map";

function trackExperimentStartedEvent(
  experimentName: string,
  variationName: string
) {
  trackEvent("$experiment_started", {
    "Experiment name": `Meristem ${experimentName}`,
    "Variant name": variationName,
  });
}

/**
 * Determine if we should fire off an Experiment Started tracking event based on the currently
 * enrolled experiment(s), eventName and properties
 */
export function maybeTrackExperimentStarted(
  eventName: string,
  properties: JsonObject
) {
  // Experiments that the user is currently enrolled in
  const experimentStates = getStoredExperiments();
  if (experimentStates) {
    experimentStates.forEach((experimentState) => {
      const { experimentName, variationName } = experimentState;
      const resolvedEventName = mapEventName(eventName);
      // Look for start event belonging to our current experiment that matches our eventName
      const startEvent = extractStartEvent(experimentName, resolvedEventName);
      const shouldFire = matchesStartEventFilters(properties, startEvent);
      if (shouldFire) {
        trackExperimentStartedEvent(experimentName, variationName);
      }
    });
  }
}

export function extractStartEvent(experimentName: string, eventName: string) {
  const meristemExperiment = getMeristemState()?.experiments?.find(
    (experiment) => experiment.name === experimentName
  );
  if (meristemExperiment && meristemExperiment.startEvents) {
    const lowerCaseEventName = eventName.toLowerCase();
    const startEvent = meristemExperiment.startEvents.find(
      (needle) => needle.name.toLowerCase() === lowerCaseEventName
    );
    if (startEvent) {
      return startEvent;
    }
  }
  return null;
}

/**
 * Determines if the provided properties matches the given startEvent
 * and its filters (if there are any)
 */
export function matchesStartEventFilters(
  properties: JsonObject,
  startEvent: ReadonlyDeep<{ filters?: Targeting[] }>
) {
  let startEventValid = false;
  if (startEvent) {
    startEventValid = true;
    // Check if the properties passed in passes the provided filters
    // Each filter behaves as an additional AND conditional
    if (startEvent.filters) {
      for (let i = 0; i < startEvent.filters.length; i += 1) {
        const { key, operator, value } = startEvent.filters[i];
        if (operator === "IS SET") {
          startEventValid = Object.prototype.hasOwnProperty.call(
            properties,
            key
          );
        } else if (operator === "EQUALS") {
          startEventValid = filterValueEquals(properties[key], value);
        } else if (operator === "IN") {
          invariant(Array.isArray(value), "value must be an array");
          startEventValid = value.some((needle) =>
            filterValueEquals(properties[key], needle)
          );
        } else if (operator === "NOT EQUALS") {
          startEventValid = !filterValueEquals(properties[key], value);
        } else if (operator === "NOT IN") {
          invariant(Array.isArray(value), "value must be an array");
          startEventValid = !value.some((needle) =>
            filterValueEquals(properties[key], needle)
          );
        }
        if (!startEventValid) {
          // Break early if a filter does not match
          break;
        }
      }
    }
  }
  return startEventValid;
}

function filterValueEquals(value: any, filterValue: any) {
  if (filterValue === "true") {
    return value === true;
  }
  if (filterValue === "false") {
    return value === false;
  }
  return value === filterValue || +filterValue === value;
}
